import { useMemo, useRef } from "react";

import type {
  AccountReportParams,
  AccountReportStates,
} from "../providers/AccountReportProvider";

import { encodeSearch } from "../../../context/urlParams";
import { decodeIdList } from "../../utils/decodeIdList";

export type DecodedAccountReportParams = {
  rebateAccountIDs: string[];
  rebateProgramIDs: string[];
};

/**
 * Every search key this hook owns and will write back to the URL on filter
 * change. Exported so the caller's `writeSearch` can strip these from the
 * previous search before merging the new `encoded` values -- preserving
 * everything else the hook has no opinion on (e.g. the portal's `lng`, the
 * backoffice route's `tab`, or the Trading report's date range).
 */
export const ACCOUNT_REPORT_SYNCED_SEARCH_KEYS = [
  "rebateAccountIDs",
  "rebateProgramIDs",
] as const satisfies readonly (keyof DecodedAccountReportParams)[];

export type UseAccountReportUrlParamsInput = {
  /** The current route search, already read by the caller (each app owns its
   * own `useSearch` call, keeping this hook framework-agnostic). */
  search: Record<string, unknown>;
  /** Writes the encoded managed-key search to the URL. The hook only emits
   * `ACCOUNT_REPORT_SYNCED_SEARCH_KEYS`; merging them into the existing
   * search without dropping unrelated keys or the hash is the caller's job. */
  writeSearch: (encoded: Record<string, string>) => void;
};

export type UseAccountReportUrlParamsResult = {
  hasUrlParams: boolean;
  decoded: DecodedAccountReportParams | undefined;
  initialState: Partial<AccountReportStates> | undefined;
  providerKey: string;
  handleParamsChange: (params: AccountReportParams) => void;
};

/**
 * Route-agnostic URL-param sync for the Account report, shared by portal and
 * backoffice -- the Account counterpart of `useTradingReportUrlParams`, minus
 * the date range (this report has none). Decodes the current search into a
 * provider `initialState` once on mount, so the Shared Rebate Programs
 * Clients hover-link opens pre-filtered, and returns a `handleParamsChange`
 * that keeps the URL in sync with every later rebate account/program change.
 */
export const useAccountReportUrlParams = ({
  search,
  writeSearch,
}: UseAccountReportUrlParamsInput): UseAccountReportUrlParamsResult => {
  const hasUrlParams =
    Boolean(search.rebateAccountIDs) || Boolean(search.rebateProgramIDs);

  const decoded = useMemo((): DecodedAccountReportParams | undefined => {
    if (!hasUrlParams) return undefined;
    return {
      rebateAccountIDs: decodeIdList(search.rebateAccountIDs),
      rebateProgramIDs: decodeIdList(search.rebateProgramIDs),
    };
  }, [hasUrlParams, search.rebateAccountIDs, search.rebateProgramIDs]);

  const initialState: Partial<AccountReportStates> | undefined = decoded
    ? {
        selected: {
          rebateAccountIDs: decoded.rebateAccountIDs.length
            ? decoded.rebateAccountIDs
            : null,
          rebateProgramIDs: decoded.rebateProgramIDs.length
            ? decoded.rebateProgramIDs
            : null,
        },
      }
    : undefined;

  // The provider reads `initialState` once, lazily, on first mount only (see
  // `createReportContext`) -- a stable key avoids remounting (and discarding
  // in-progress edits) on every later URL write from `handleParamsChange`.
  const providerKey = useRef(
    hasUrlParams ? "url-params" : "no-url-params",
  ).current;

  const lastWrittenSearchRef = useRef<string | null>(null);

  const handleParamsChange = (params: AccountReportParams) => {
    const encoded = encodeSearch(
      {
        rebateAccountIDs: params.selected.rebateAccountIDs,
        rebateProgramIDs: params.selected.rebateProgramIDs,
      },
      { from: null, to: null },
    );
    const nextKey = JSON.stringify(encoded);
    if (lastWrittenSearchRef.current === nextKey) return;
    lastWrittenSearchRef.current = nextKey;
    writeSearch(encoded);
  };

  return {
    hasUrlParams,
    decoded,
    initialState,
    providerKey,
    handleParamsChange,
  };
};
